// ============================================================================
// NumberStepper — premium numeric input for the design system.
// ----------------------------------------------------------------------------
// Replaces `<TextInput type="number">` where a stepper feels better than a
// text field. Visual variant "orbit" provides a premium number picker:
//   - "orbit"  → two chevron pills flanking a large centered value.
// Features:
//   - Support direct typing (numeric/decimal keyboard on mobile).
//   - Clamp to [min, max].
//   - Repeat on press-and-hold (200ms then 60ms).
//   - Increase by 10× when the user holds Shift.
//   - Fire onChange with the clamped number.
//   - Respect prefers-reduced-motion for the ripple.
// ============================================================================

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { Pressable } from './Pressable'

export type NumberStepperVariant = 'orbit'
export type NumberStepperSize = 'sm' | 'md' | 'compact'

interface Props {
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  step?: number
  variant?: NumberStepperVariant
  size?: NumberStepperSize
  disabled?: boolean
  suffix?: string
  ariaLabel?: string
  className?: string
  /** Optional multiplier applied when the user holds Shift while pressing the
   *  arrows or when using Arrow keys — mirrors native <input type=number>. */
  shiftMultiplier?: number
}

const HOLD_INITIAL_MS = 260
const HOLD_REPEAT_MS = 60

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

export const NumberStepper = forwardRef<HTMLInputElement, Props>(function NumberStepper(
  {
    value,
    onChange,
    min = -Infinity,
    max = Infinity,
    step = 1,
    variant = 'orbit',
    size = 'md',
    disabled = false,
    suffix,
    ariaLabel,
    className,
    shiftMultiplier = 10,
  },
  ref,
) {
  const [draft, setDraft] = useState<string>(String(value))
  const holdTimer = useRef<number | null>(null)
  const holdInterval = useRef<number | null>(null)

  useEffect(() => {
    // Keep the visible draft in sync when the caller updates the value
    // externally (blur, clamp, or programmatic set).
    setDraft(String(value))
  }, [value])

  const commit = useCallback(
    (nextRaw: number) => {
      if (Number.isNaN(nextRaw)) return
      const clamped = clamp(nextRaw, min, max)
      if (clamped !== value) onChange(clamped)
      setDraft(String(clamped))
    },
    [max, min, onChange, value],
  )

  const bumpBy = useCallback(
    (delta: number) => {
      commit(clamp((Number(value) || 0) + delta, min, max))
    },
    [commit, max, min, value],
  )

  const stopHold = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
    if (holdInterval.current !== null) {
      window.clearInterval(holdInterval.current)
      holdInterval.current = null
    }
  }, [])

  useEffect(() => stopHold, [stopHold])

  const startHold = (direction: 1 | -1, event: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return
    const shiftHeld = 'shiftKey' in event && (event as React.MouseEvent).shiftKey
    const magnitude = step * (shiftHeld ? shiftMultiplier : 1)
    bumpBy(direction * magnitude)
    holdTimer.current = window.setTimeout(() => {
      holdInterval.current = window.setInterval(() => {
        bumpBy(direction * magnitude)
      }, HOLD_REPEAT_MS)
    }, HOLD_INITIAL_MS)
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      bumpBy(step * (event.shiftKey ? shiftMultiplier : 1))
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      bumpBy(-step * (event.shiftKey ? shiftMultiplier : 1))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const parsed = Number(draft)
      if (!Number.isNaN(parsed)) commit(parsed)
    }
  }

  const wrapperClass = useMemo(
    () => [
      'dsStep',
      `dsStep--${variant}`,
      `dsStep--${size}`,
      disabled ? 'is-disabled' : '',
      className ?? '',
    ].filter(Boolean).join(' '),
    [className, disabled, size, variant],
  )

  const decrementDisabled = disabled || value <= min
  const incrementDisabled = disabled || value >= max

  const decrementButton = (
    <Pressable
      className="dsStep__btn dsStep__btn--down"
      aria-label="Decrease"
      tabIndex={-1}
      disabled={decrementDisabled}
      onMouseDown={(event) => startHold(-1, event)}
      onMouseUp={stopHold}
      onMouseLeave={stopHold}
      onTouchStart={(event) => startHold(-1, event)}
      onTouchEnd={stopHold}
      onContextMenu={(event) => event.preventDefault()}
    >
      <KeyboardArrowDownRoundedIcon fontSize="inherit" />
    </Pressable>
  )

  const incrementButton = (
    <Pressable
      className="dsStep__btn dsStep__btn--up"
      aria-label="Increase"
      tabIndex={-1}
      disabled={incrementDisabled}
      onMouseDown={(event) => startHold(1, event)}
      onMouseUp={stopHold}
      onMouseLeave={stopHold}
      onTouchStart={(event) => startHold(1, event)}
      onTouchEnd={stopHold}
      onContextMenu={(event) => event.preventDefault()}
    >
      <KeyboardArrowUpRoundedIcon fontSize="inherit" />
    </Pressable>
  )

  const input = (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      pattern="-?[0-9]*(\.[0-9]+)?"
      className="dsStep__field"
      value={draft}
      onChange={(event) => setDraft(event.target.value.replace(/[^\d.-]/g, ''))}
      onBlur={() => {
        const parsed = Number(draft)
        if (Number.isNaN(parsed)) setDraft(String(value))
        else commit(parsed)
      }}
      onKeyDown={onKeyDown}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-valuemin={Number.isFinite(min) ? min : undefined}
      aria-valuemax={Number.isFinite(max) ? max : undefined}
      aria-valuenow={value}
      role="spinbutton"
    />
  )

  const suffixNode = suffix ? <span className="dsStep__suffix" aria-hidden="true">{suffix}</span> : null

  // orbit
  return (
    <span className={wrapperClass}>
      <span className="dsStep__valueGroup">
        {input}
        {suffixNode}
      </span>
      {decrementButton}
      {incrementButton}
    </span>
  )
})
